import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { EnrichedTag } from "../../shared/types.ts";
import { useApp } from "../context/AppContext.tsx";

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: EnrichedTag[];
  disabled?: boolean;
  compact?: boolean;
}

// Bound the rendered list so a huge tag library can't blow up the DOM. The
// dropdown is scrollable, so anything past this is reachable by typing more.
const MAX_SUGGESTIONS = 50;

// Lower rank = better match. Exact beats prefix beats word-start beats a
// substring buried mid-word, so typing "test" surfaces "test" above
// "abtesting" / "contest".
function matchRank(value: string, query: string): number {
  if (value === query) return 0;
  if (value.startsWith(query)) return 1;
  // Word-start match: query begins a word inside the tag (after a separator).
  if (value.split(/[\s\-_/.]+/).some((word) => word.startsWith(query))) {
    return 2;
  }
  return 3;
}

// Split a tag value around the first occurrence of the query so the matched
// run can be emphasized in the dropdown.
function TagLabel({ value, query }: { value: string; query: string }) {
  const idx = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (idx === -1) {
    return <span className="text-gray-700">{value}</span>;
  }
  return (
    <span className="text-gray-400">
      {value.slice(0, idx)}
      <span className="font-semibold text-gray-900">
        {value.slice(idx, idx + query.length)}
      </span>
      {value.slice(idx + query.length)}
    </span>
  );
}

export function TagInput({
  tags,
  onTagsChange,
  availableTags,
  disabled = false,
  compact = false,
}: TagInputProps) {
  const { trackTagUsage, recentTags } = useApp();
  const [tagInput, setTagInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  // -1 means "nothing highlighted" — Enter then creates/adds the typed text
  // rather than silently grabbing the first suggestion.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const tagsLower = tags.map((t) => t.toLowerCase());

  // Recency index for tie-breaking (most-recently-used first).
  const recentRank = useMemo(() => {
    const map = new Map<string, number>();
    recentTags.forEach((t, i) => map.set(t.toLowerCase(), i));
    return map;
  }, [recentTags]);

  const suggestions = useMemo(() => {
    const query = tagInput.trim().toLowerCase();
    // No dropdown until the user types — an empty query would match the whole
    // library and just show a wall of arbitrary tags.
    if (!query) return [];
    return availableTags
      .filter((tag) => {
        const v = tag.value.toLowerCase();
        return v.includes(query) && !tagsLower.includes(v);
      })
      .map((tag) => {
        const v = tag.value.toLowerCase();
        return { tag, rank: matchRank(v, query), idx: v.indexOf(query) };
      })
      .sort((a, b) => {
        // Best tier first (exact → prefix → word-start → substring), then the
        // earliest match position (so "test" puts abtesting above contest),
        // then most-recently-used, then shorter, then alphabetical.
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.idx !== b.idx) return a.idx - b.idx;
        const ra = recentRank.get(a.tag.value.toLowerCase()) ?? Infinity;
        const rb = recentRank.get(b.tag.value.toLowerCase()) ?? Infinity;
        if (ra !== rb) return ra - rb;
        if (a.tag.value.length !== b.tag.value.length) {
          return a.tag.value.length - b.tag.value.length;
        }
        return a.tag.value.localeCompare(b.tag.value);
      })
      .slice(0, MAX_SUGGESTIONS)
      .map((entry) => entry.tag);
    // tagsLower is derived from `tags`; depend on its joined form to stay stable.
  }, [availableTags, tagInput, tags.join(" "), recentRank]);

  // Keep the highlighted option scrolled into view during keyboard nav.
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightedIndex] as
      | HTMLElement
      | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  function handleAddTag(tagValue: string) {
    if (!tagValue) return;
    // Check case-insensitively if already added
    if (tagsLower.includes(tagValue.toLowerCase())) return;
    // Use existing tag casing if available
    const existing = availableTags.find(
      (t) => t.value.toLowerCase() === tagValue.toLowerCase(),
    );
    const resolved = existing ? existing.value : tagValue;
    trackTagUsage(resolved);
    onTagsChange([...tags, resolved]);
    setTagInput("");
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }

  function handleRemoveTag(tagValue: string) {
    // Intentional: removing a tag still counts as engagement, so it surfaces
    // in the Recent zone alongside add/toggle. "Tags I'm working with" is
    // the model, not "tags I'm filtering by."
    trackTagUsage(tagValue);
    onTagsChange(tags.filter((t) => t !== tagValue));
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length === 0) return;
      setShowSuggestions(true);
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        handleAddTag(suggestions[highlightedIndex].value);
      } else if (tagInput.trim()) {
        handleAddTag(tagInput.trim());
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  }

  const inputPadding = compact ? "px-3 py-2" : "px-4 py-3";

  return (
    <div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="text-gray-500 hover:text-red-600 ml-1"
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={tagInput}
          role="combobox"
          aria-expanded={showSuggestions && suggestions.length > 0}
          aria-autocomplete="list"
          aria-activedescendant={highlightedIndex >= 0
            ? `tag-option-${highlightedIndex}`
            : undefined}
          onChange={(e) => {
            setTagInput(e.target.value);
            setShowSuggestions(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Add tags..."
          className={`w-full ${inputPadding} rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition`}
          disabled={disabled}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={listRef}
            role="listbox"
            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            {suggestions.map((tag, i) => (
              <button
                key={tag.uri}
                id={`tag-option-${i}`}
                type="button"
                role="option"
                aria-selected={i === highlightedIndex}
                // Keep input focus so onBlur doesn't close before the click.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => handleAddTag(tag.value)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  i === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
              >
                <TagLabel value={tag.value} query={tagInput.trim()} />
              </button>
            ))}
          </div>
        )}
      </div>

      {!compact && (
        <p className="text-xs text-gray-500 mt-2">
          Type to search existing tags or press Enter to create a new one
        </p>
      )}
    </div>
  );
}
